<template>
    <jet-authentication-card>

        <jet-banner />

        <template #logo>
            <jet-authentication-card-logo :name="$page.props.app_name.toLowerCase()" />
        </template>

        <jet-validation-errors class="mb-4" />

        <div v-if="status" class="mb-4 font-medium text-sm text-green-600">
            {{ status }}
        </div>

        <form @submit.prevent="submit">


            <div v-if="!sso_checked" class="flex items-center justify-end mt-4">
                <a :href="route('google.auth')" title="Sign in with Google"
                    class="relative flex flex-row items-stretch overflow-hidden w-full items-center bg-blue-500 border-2 border-blue-500 rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-400 active:bg-blue-600 focus:outline-none focus:border-blue-600 focus:ring focus:ring-blue-400 disabled:opacity-25 transition">
                    <div class=" absolute left-0 h-full bg-white text-blue-500 w-12 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" class="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
                          <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                            <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"/>
                            <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"/>
                            <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"/>
                            <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"/>
                          </g>
                        </svg>
                    </div>
                    <div class="flex flex-grow py-2 ml-3 justify-center">
                        Sign in with Google
                    </div>
                </a>
            </div>

            <div v-if="!sso_checked" id="g_id_onload"
                data-client_id="387274310925-s61aeo8fem7158h6dp6mpe4mtajpqi5p.apps.googleusercontent.com"
                data-auto_select="true"
                data-login_uri="/google/auth">
            </div>

            <div v-if="!sso_checked">
                <div class="py-8">
                    <div class="border-t border-gray-200 relative flex justify-center">
                        <div class="absolute bg-white text-gray-300 -mt-3 px-2">OR</div>
                    </div>
                </div>
            </div>


            
            <div v-if="!sso_checked">
                <jet-label for="email" value="Email" />
                <jet-input id="email" type="email" class="mt-1 block w-full" v-model="form.email" required autofocus autocomplete="username" />
            </div>
            
            <div v-if="!sso_checked" class="flex items-center justify-end mt-6 mb-4">
                <jet-button class="w-full" :class="{ 'opacity-25': !email_valid }" :disabled="!email_valid" @click.prevent.stop="testSSO">
                    Sign in to Birdie
                </jet-button>
            </div>

            
            <div v-if="sso_checked && !sso_redirect" class="">

                <input type="hidden" name="username" :value="form.email" />
                <div class="mt-4">
                    <jet-label for="password" value="Password" />
                    <jet-input id="password" type="password" class="mt-1 block w-full" v-model="form.password" required autocomplete="current-password" />
                </div>

                <div class="mt-4 flex flex-row justify-between">
                    <label class="flex items-center">
                        <jet-checkbox name="remember" v-model:checked="form.remember" />
                        <span class="ml-2 text-sm text-gray-600">Remember me</span>
                    </label>

                    <inertia-link v-if="canResetPassword || true" :href="route('password.request')" class="underline text-sm text-gray-600 hover:text-gray-900">
                        Forgot your password?
                    </inertia-link>

                </div>

                <div class="flex items-center justify-end mt-4 mb-4">
                    <jet-button class="w-full" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                        Sign in to Birdie
                    </jet-button>
                </div>

            </div>

            <div v-else-if="sso_checked && sso_redirect" class="">

                <div class="flex items-center justify-center text-gray-500 text-lg font-bold"><LockClosedIcon class="h-5 w-5 mr-2" /> Single Sign-On</div>
                
                <div class="flex items-center justify-end mt-4 mb-4">
                    <a :href="sso_redirect" class="inline-flex items-center justify-center px-4 py-2 bg-gray-800 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 active:bg-gray-900 focus:outline-none focus:border-gray-900 focus:ring focus:ring-gray-300 disabled:opacity-25 disabled:cursor-default transition w-full">
                        Sign-in with {{ team_name }} SSO <ArrowRightIcon class="w-4 h-4 ml-2" />
                    </a>
                </div>

            </div>

        </form>

        <template #footer>
            <div v-if="!sso_checked || !sso_redirect" 
                class="mt-5 text-gray-600">
                    No account yet? 👉 <inertia-link :href="route('letsgo.start')"          class="underline text-gray-600 hover:text-gray-900">
                Sign up
                </inertia-link>
            </div>
        </template>

    </jet-authentication-card>



</template>

<script>
    import JetAuthenticationCard from '@/Jetstream/AuthenticationCard'
    import JetAuthenticationCardLogo from '@/Jetstream/AuthenticationCardLogo'
    import JetButton from '@/Jetstream/Button'
    import JetInput from '@/Jetstream/Input'
    import JetCheckbox from '@/Jetstream/Checkbox'
    import JetLabel from '@/Jetstream/Label'
    import JetValidationErrors from '@/Jetstream/ValidationErrors'
    import JetSectionBorder from '@/Jetstream/SectionBorder'
    import JetBanner from '@/Jetstream/Banner'
    import { ArrowRightIcon, LockClosedIcon } from "@heroicons/vue/24/outline";


    export default {
        components: {
            JetAuthenticationCard,
            JetAuthenticationCardLogo,
            JetButton,
            JetInput,
            JetCheckbox,
            JetLabel,
            JetValidationErrors,
            JetSectionBorder,
            JetBanner, 
            ArrowRightIcon,
            LockClosedIcon
        },

        props: {
            canResetPassword: Boolean,
            status: String
        },

        created() {
            if (this.$page.props.user_email) {
                this.form.email = this.$page.props.user_email
            }
        },

        data() {
            return {
                form: this.$inertia.form({
                    email: '',
                    password: '',
                    remember: false
                }),
                is_oauth: this.$page.props.oauth === 'oauth',
                //sso_team: false,
                sso_checked: false,
                team_name: null,
                sso_name: null,
                sso_redirect: null,
            }
        },

        methods: {
            testSSO() {
                if (!this.email_valid) {
                    return
                }
                var params = {
                    email: this.form.email,
                };
                let url = this.$page.props.api_url+'/v1/team/sso'
                axios.post(url, params)
                .then(response => {
                    console.log(response.data);
                    if (response.data.team_name && response.data.sso_name && response.data.sso_redirect) {
                        this.team_name = response.data.team_name
                        this.sso_name = response.data.sso_name
                        this.sso_redirect = response.data.sso_redirect
                    }
                    this.sso_checked = true
                })
                .catch(e => {
                    console.error(e);
                });
            },
            submit() {
                /*if (this.form.remember) {
                    this.form.remember = 'on'
                } else {
                    this.form.remember = ''
                }
                axios.post(this.route('login'), this.form)
                  .then(res => {
                    console.log(res)
                    if (res.status == 200) {
                        this.form.reset('password')
                        this.$inertia.visit(route('home'))
                    }

                })
                return*/


                // if (this.is_oauth) {
                //     let formdata = {
                //         remember: this.form.remember ? 'on' : '',
                //         email: this.form.email ? this.form.email : '',
                //         password: this.form.password ? this.form.password : '',
                //     }
                //     this.$inertia.visit('/login', {
                //         method: 'post',
                //         data: formdata,
                //         replace: true,
                //     })
                // } else {

                    this.form
                        .transform(data => ({
                            ... data,
                            remember: this.form.remember ? 'on' : '',
                            _token: this.$page.props.csrf_token
                        }))
                        .post(this.route('login'), {
                            onFinish: () => {
                                this.form.reset('password')
                                //console.log("onFinish")
                            }
                        })
                // }
            },
            validateNotempty: function(value) {
                var re = /^\s*\S+.*/;
                return re.test(value);
            },
            validateEmail: function(value) {
                var re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                return re.test(value);
            }
        },
        computed: {
            email_valid() {
                return this.validateEmail(this.form.email)
            },
        }
    }
</script>
